import { IdentifyCollectionStep, Type } from '@/constants/Steps';
import { SurveyState } from '@/stores/survey';

export const formatNumber = (
  number: number | null | undefined,
  minimumFractionDigits = 0,
  maximumFractionDigits = minimumFractionDigits,
) => {
  if (!number) {
    return 0;
  }
  return new Intl.NumberFormat('default', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(number);
};

export const formatCoefficient = (
  number: number | null | undefined,
  {
    minimumSignificantDigits = 2,
    maximumSignificantDigits = minimumSignificantDigits,
  }: {
    minimumSignificantDigits?: number;
    maximumSignificantDigits?: number;
  } = {},
) => {
  if (!number) {
    return 0;
  }
  return new Intl.NumberFormat('default', {
    minimumSignificantDigits,
    maximumSignificantDigits,
  }).format(number);
};

export const formatPercent = (
  number: number | null | string,
  minimumFractionDigits = 0,
  maximumFractionDigits = 0,
) => {
  const asNumber = Number(number);
  if (isNaN(asNumber)) {
    return '—';
  }
  const formatted = formatNumber(
    asNumber ? asNumber * 100 : asNumber,
    minimumFractionDigits,
    maximumFractionDigits,
  );
  return formatted.toString() + '%';
};

export const numberToOrdinal = (
  rank: number | 'NAN' | null,
  inlcudeMedal = true,
) => {
  if (!rank || rank === 'NAN') {
    return '—';
  }
  const tens = rank % 10,
    hundreds = rank % 100;
  if (tens === 1 && hundreds !== 11) {
    return (rank === 1 && inlcudeMedal ? '🥇 ' : '') + rank + 'st';
  }
  if (tens === 2 && hundreds !== 12) {
    return (rank === 2 && inlcudeMedal ? '🥈 ' : '') + rank + 'nd';
  }
  if (tens === 3 && hundreds !== 13) {
    return (rank === 3 && inlcudeMedal ? '🥉 ' : '') + rank + 'rd';
  }
  return rank + 'th';
};

export const participantToDisplayName = (
  participant: Record<string, any> | null,
  identifyStepId?: string | null,
) => {
  const identifyStepAnswer = participant?.identifyAnswers?.find(
    ({ step }: any) => step === identifyStepId,
  );
  if (identifyStepAnswer) {
    return identifyStepAnswer.answer;
  }
  if (!participant) {
    return 'Admin';
  }

  if (participant.email) {
    return participant.email;
  }

  return `Participant ${participant?.number}`;
};

export const getImageUrl = (imagePath: string) => {
  return new URL(imagePath, import.meta.url).href;
};

export const getFirstIdentifyStep = (survey: SurveyState) => {
  const identifyCollectionStepTypes: IdentifyCollectionStep[] = [
    Type.EMAIL_COLLECTION,
    Type.NAME_COLLECTION,
    Type.USERNAME_COLLECTION,
  ];
  for (const identifyCollectionStepType of identifyCollectionStepTypes) {
    const step = survey.steps.find(
      ({ type }) => type === identifyCollectionStepType,
    );
    if (step) {
      return step._id;
    }
  }
  return null;
};

export const toValidHref = (link: string) => {
  return '//' + link.replace(/(^\w+:|^)\/\//, '');
};

export const toValidURL = (link: string) => {
  try {
    return new URL(link);
  } catch {
    return new URL('https://' + link.replace(/(^\w+:|^)\/\//, ''));
  }
};

export const toBase26String = (num: number) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let str = '';
  while (num >= 0) {
    str = alphabet[num % 26] + str;
    num = Math.floor(num / 26) - 1;
  }
  return str;
};

export const excelColumnNames = (count: number) => {
  const result = [];

  for (let i = 0; i < count; i++) {
    result.push(toBase26String(i));
  }

  return result;
};
